import * as React from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import FormHelperText from '@mui/joy/FormHelperText';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import IconButton from '@mui/joy/IconButton';
import Autocomplete from '@mui/joy/Autocomplete';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Select from '@mui/joy/Select';
import Sheet from '@mui/joy/Sheet';
import Option from '@mui/joy/Option';
import Radio from '@mui/joy/Radio';
import RadioGroup from '@mui/joy/RadioGroup';
import Stack from '@mui/joy/Stack';
import Table from '@mui/joy/Table';
import Textarea from '@mui/joy/Textarea';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import Alert from '@mui/joy/Alert';
import { useTheme } from '@mui/joy/styles';
import { CalendarMonthRounded, EditRounded, FavoriteRounded, FileDownloadRounded, PersonAddAltRounded, RefreshRounded, SearchRounded, VisibilityRounded } from '@mui/icons-material';
import states from '../data/states.json';
import cities from '../data/state_cities.json';

import ExportModal, { ExportColumn } from '../components/nitya-seva/ExportModal';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';
import {
  fetchNityaSevaEnrollments,
  fetchNityaSevaEnrollmentDetail,
  fetchNityaSevaMonthDetail,
  fetchNityaSevaMonthSummary,
  NityaSevaEnrollment,
  NityaSevaPaymentFrequency,
  NityaSevaSearchUserItem,
  NityaSevaUserLookupResponse,
  NityaSevaMonthSummary,
  createOfflineNityaSevaEnrollment,
  searchNityaSevaUsersByPhone,
  updateNityaSevaEnrollment,
  recordNityaSevaManualDonation,
} from '../api';

const modeLabel: Record<string, string> = {
  ecs_nach: 'ECS / NACH',
  auto_razorpay: 'Auto Razorpay',
  online_transfer: 'Online Transfer',
  cash_first_sunday: 'Cash 1st Sunday',
};

const getEnrollmentSourceLabel = (value?: string) => (
  value === 'offline' ? 'Offline · Added by admin' : 'Online · Self-enrolled'
);

const getEnrollmentSourceColor = (value?: string): 'warning' | 'primary' => (
  value === 'offline' ? 'warning' : 'primary'
);

const currentMonthPaymentChip = (value?: { label: string; color: 'success' | 'danger' | 'neutral' }) => (
  value || { label: 'Unavailable', color: 'neutral' as const }
);

type SortDirection = 'asc' | 'desc';

type EnrollmentSortKey = 'fullName' | 'whatsAppNumber' | 'phone' | 'email' | 'submissionMode' | 'pledgeAmount' | 'status' | 'currentMonth' | 'lastManualDonationAt';
type MonthSortKey = 'month' | 'paidCount' | 'dueCount' | 'totalEnrollments' | 'manualCount' | 'subscriptionCount' | 'totalExpected' | 'totalCollected' | 'pendingAmount';

const compareText = (a?: string | number | null, b?: string | number | null) => (
  String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' })
);

const compareNumbers = (a?: number | string | null, b?: number | string | null) => (
  Number(a ?? 0) - Number(b ?? 0)
);

const compareDates = (a?: string | null, b?: string | null) => {
  const first = a ? dayjs(a).valueOf() : 0;
  const second = b ? dayjs(b).valueOf() : 0;
  return first - second;
};

const applySortDirection = (value: number, direction: SortDirection) => (
  direction === 'asc' ? value : -value
);

const renderSortableHeader = (
  label: string,
  active: boolean,
  direction: SortDirection,
  onClick: () => void,
) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      all: 'unset',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      color: active ? 'primary.600' : 'inherit',
      font: 'inherit',
      fontWeight: 700,
      '&:hover': { color: 'primary.500' },
    }}
  >
    <span>{label}</span>
    <Box component="span" aria-hidden="true" sx={{ fontSize: '0.8rem' }}>
      {active ? (direction === 'asc' ? '↑' : '↓') : '↕'}
    </Box>
  </Box>
);

const pledgeTierThresholds = [
  { tier: 'Sevak', minAmount: 1 },
  { tier: 'Sri Srivasa Sevak', minAmount: 1100 },
  { tier: 'Sri Gadadhar Sevak', minAmount: 2100 },
  { tier: 'Sri Advaita Sevak', minAmount: 3100 },
  { tier: 'Sri Nityananda Sevak', minAmount: 5100 },
  { tier: 'Sri Gauranga Sevak', minAmount: 11000 },
] as const;

const getPledgeTierFromAmount = (amount: number) => {
  let selectedTier: (typeof pledgeTierThresholds)[number]['tier'] = pledgeTierThresholds[0].tier;

  for (const option of pledgeTierThresholds) {
    if (amount >= option.minAmount) {
      selectedTier = option.tier;
    }
  }

  return selectedTier;
};

const paymentFrequencyOptions: Array<{ value: NityaSevaPaymentFrequency; label: string; months: number }> = [
  { value: 'monthly', label: 'Monthly', months: 1 },
  { value: 'quarterly', label: 'Quarterly', months: 3 },
  { value: 'half_yearly', label: 'Half-Yearly', months: 6 },
  { value: 'annually', label: 'Annually', months: 12 },
];

const getPaymentFrequencyMonths = (value: NityaSevaPaymentFrequency) => paymentFrequencyOptions.find((option) => option.value === value)?.months || 1;
const getPaymentFrequencyLabel = (value: NityaSevaPaymentFrequency) => paymentFrequencyOptions.find((option) => option.value === value)?.label || 'Monthly';
const getCycleAmount = (monthlyAmount: number, value: NityaSevaPaymentFrequency) => Number(monthlyAmount || 0) * getPaymentFrequencyMonths(value);

type OfflineEnrollmentForm = {
  fullName: string;
  phone: string;
  whatsAppNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  dateOfBirth: string;
  anniversaryDate: string;
  startDate: string;
  pledgeAmount: number;
  paymentFrequency: NityaSevaPaymentFrequency;
  submissionMode: NityaSevaEnrollment['submissionMode'];
  panNumber: string;
  status: string;
  adminNote: string;
};

type EditEnrollmentForm = {
  fullName: string;
  phone: string;
  whatsAppNumber: string;
  email: string;
  address: string;
  dateOfBirth: string;
  anniversaryDate: string;
  startDate: string;
  pledgeAmount: number;
  paymentFrequency: NityaSevaPaymentFrequency;
  submissionMode: NityaSevaEnrollment['submissionMode'];
  panNumber: string;
  status: string;
  adminNote: string;
};

const defaultOfflineEnrollmentForm: OfflineEnrollmentForm = {
  fullName: '',
  phone: '',
  whatsAppNumber: '',
  email: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
  dateOfBirth: '',
  anniversaryDate: '',
  startDate: dayjs().format('YYYY-MM-DD'),
  pledgeAmount: 0,
  paymentFrequency: 'monthly',
  submissionMode: 'cash_first_sunday',
  panNumber: '',
  status: 'pending',
  adminNote: '',
};

const digitsOnly = (value: string) => value.replace(/\D+/g, '');
const parsePledgeAmount = (value: string) => {
  const digits = value.replace(/\D+/g, '');
  return digits ? Number.parseInt(digits, 10) : 0;
};
const emailRegex = /^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const cityRegex = /^[A-Z ]+$/;
const addressRegex = /^[a-zA-Z0-9 .,\-\/]*$/;

const toLocalPhone = (value: string) => {
  const digits = digitsOnly(value);
  if (digits.length === 12 && digits.startsWith('91')) {
    return digits.slice(2);
  }
  if (digits.length > 10) {
    return digits.slice(-10);
  }
  return digits;
};

const normalizeDateInput = (value?: string | null) => {
  if (!value || value.startsWith('0001-01-01')) {
    return '';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

export default function NityaSevaAdmin() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const datePickerInputSx = React.useMemo(() => ({
    '& input[type="date"], & input[type="month"]': {
      colorScheme: isDark ? 'dark' : 'light',
      accentColor: isDark ? '#38bdf8' : '#0ea5e9',
    },
    '& input[type="date"]::-webkit-calendar-picker-indicator, & input[type="month"]::-webkit-calendar-picker-indicator': {
      cursor: 'pointer',
    },
  }), [isDark]);

  const navigate = useNavigate();
  const [items, setItems] = React.useState<NityaSevaEnrollment[]>([]);
  const [monthItems, setMonthItems] = React.useState<NityaSevaMonthSummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [monthLoading, setMonthLoading] = React.useState(false);
  const [currentMonthStatusLoading, setCurrentMonthStatusLoading] = React.useState(false);
  const [currentMonthStatusUnavailable, setCurrentMonthStatusUnavailable] = React.useState(false);
  const [currentMonthStatusByEnrollmentId, setCurrentMonthStatusByEnrollmentId] = React.useState<Record<number, { label: string; color: 'success' | 'danger' | 'neutral' }>>({});
  const [viewMode, setViewMode] = React.useState<'enrollments' | 'months'>('enrollments');
  const [status, setStatus] = React.useState('');
  const [mode, setMode] = React.useState('');
  const [q, setQ] = React.useState('');
  const [enrollmentSourceFilter, setEnrollmentSourceFilter] = React.useState('');
  const [paymentFilter, setPaymentFilter] = React.useState('');
  const [enrollmentSort, setEnrollmentSort] = React.useState<{ key: EnrollmentSortKey; direction: SortDirection }>({ key: 'fullName', direction: 'asc' });
  const [monthSort, setMonthSort] = React.useState<{ key: MonthSortKey; direction: SortDirection }>({ key: 'month', direction: 'desc' });
  const [error, setError] = React.useState('');
  const [monthError, setMonthError] = React.useState('');
  const [monthApiAvailable, setMonthApiAvailable] = React.useState(true);

  const [editTarget, setEditTarget] = React.useState<NityaSevaEnrollment | null>(null);
  const [editForm, setEditForm] = React.useState<EditEnrollmentForm | null>(null);

  const [manualTarget, setManualTarget] = React.useState<NityaSevaEnrollment | null>(null);
  const [manualAmount, setManualAmount] = React.useState('');
  const [manualDate, setManualDate] = React.useState(dayjs().format('YYYY-MM-DD'));
  const [manualForMonth, setManualForMonth] = React.useState(dayjs().format('YYYY-MM'));
  const [manualPaymentFrequency, setManualPaymentFrequency] = React.useState<NityaSevaPaymentFrequency>('monthly');
  const [manualMethod, setManualMethod] = React.useState('Cash');
  const [manualTransactionId, setManualTransactionId] = React.useState('');
  const [manualNote, setManualNote] = React.useState('');
  const [savingManualDonation, setSavingManualDonation] = React.useState(false);

  const [exportOpen, setExportOpen] = React.useState(false);

  const [offlineOpen, setOfflineOpen] = React.useState(false);
  const [offlineSaving, setOfflineSaving] = React.useState(false);
  const [offlineError, setOfflineError] = React.useState('');
  const [offlineLookup, setOfflineLookup] = React.useState<NityaSevaUserLookupResponse | null>(null);
  const [phoneSuggestions, setPhoneSuggestions] = React.useState<NityaSevaSearchUserItem[]>([]);
  const [phoneSuggestionLoading, setPhoneSuggestionLoading] = React.useState(false);
  const [phoneSuggestionError, setPhoneSuggestionError] = React.useState('');
  const [offlineFieldErrors, setOfflineFieldErrors] = React.useState<Record<string, string>>({});
  const [offlineForm, setOfflineForm] = React.useState<OfflineEnrollmentForm>(defaultOfflineEnrollmentForm);
  const lastAutoSelectedRef = React.useRef('');
  const hasSelectedExistingUser = Boolean(offlineLookup?.found && offlineLookup.user?.id && !offlineLookup?.ambiguous);
  const existingUserIdentityLocked = Boolean(offlineLookup?.found && offlineLookup.user?.id);
  const existingEmailLocked = Boolean(hasSelectedExistingUser && offlineLookup?.user?.email && offlineLookup.user.email.trim());

  const cityOptions = React.useMemo(() => {
    const selectedState = offlineForm.state;
    if (selectedState && cities[selectedState as keyof typeof cities]) {
      return Array.from(new Set(cities[selectedState as keyof typeof cities]));
    }
    return Array.from(new Set(Object.values(cities).flat()));
  }, [offlineForm.state]);

  const loadEnrollments = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const enrollmentData = await fetchNityaSevaEnrollments({ status, mode, q, limit: 200 });
      setItems(Array.isArray(enrollmentData) ? enrollmentData : []);
    } catch (e) {
      setError('Failed to load Nitya Seva enrollments.');
    } finally {
      setLoading(false);
    }
  }, [status, mode, q]);

  const loadMonthSummary = React.useCallback(async () => {
    setMonthLoading(true);
    setMonthError('');
    try {
      const monthData = await fetchNityaSevaMonthSummary({ status, mode, q, limit: 500 });
      setMonthItems(Array.isArray(monthData) ? monthData : []);
      setMonthApiAvailable(true);
    } catch (e) {
      setMonthItems([]);
      setMonthApiAvailable(false);
      setMonthError('Month-wise view is not available until the backend is updated and restarted.');
    } finally {
      setMonthLoading(false);
    }
  }, [status, mode, q]);

  const loadCurrentMonthStatuses = React.useCallback(async () => {
    setCurrentMonthStatusLoading(true);
    setCurrentMonthStatusUnavailable(false);
    try {
      const currentMonth = dayjs().format('YYYY-MM');
      const detail = await fetchNityaSevaMonthDetail(currentMonth, { status, mode, q, limit: 500 });
      const nextMap: Record<number, { label: string; color: 'success' | 'danger' | 'neutral' }> = {};
      (Array.isArray(detail?.items) ? detail.items : []).forEach((entry) => {
        const paid = entry?.history?.kind !== 'due';
        nextMap[entry.enrollment.id] = {
          label: paid ? 'Paid this month' : 'Not paid yet',
          color: paid ? 'success' : 'danger',
        };
      });
      setCurrentMonthStatusByEnrollmentId(nextMap);
    } catch (_e) {
      setCurrentMonthStatusUnavailable(true);
      setCurrentMonthStatusByEnrollmentId({});
    } finally {
      setCurrentMonthStatusLoading(false);
    }
  }, [status, mode, q]);

  React.useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  React.useEffect(() => {
    if (viewMode === 'months') {
      loadMonthSummary();
    }
  }, [viewMode, loadMonthSummary]);

  React.useEffect(() => {
    loadCurrentMonthStatuses();
  }, [loadCurrentMonthStatuses]);

  const handleEnrollmentSort = React.useCallback((key: EnrollmentSortKey) => {
    setEnrollmentSort((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    ));
  }, []);

  const handleMonthSort = React.useCallback((key: MonthSortKey) => {
    setMonthSort((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    ));
  }, []);

  const getCurrentMonthPaymentState = React.useCallback((item: NityaSevaEnrollment): 'paid' | 'unpaid' | 'other' => {
    if (item.status === 'paused') {
      return 'other';
    }

    const label = (currentMonthStatusByEnrollmentId[item.id]?.label || '').toLowerCase();
    if (label.includes('not paid') || label.includes('unpaid')) {
      return 'unpaid';
    }
    if (label.includes('paid')) {
      return 'paid';
    }
    return 'other';
  }, [currentMonthStatusByEnrollmentId]);

  const matchesActiveFilters = React.useCallback((item: NityaSevaEnrollment) => {
    if (status && (item.status || '').toLowerCase() !== status.toLowerCase()) {
      return false;
    }
    if (mode && item.submissionMode !== mode) {
      return false;
    }
    if (enrollmentSourceFilter && (item.enrollmentSource || 'online') !== enrollmentSourceFilter) {
      return false;
    }
    if (paymentFilter && !currentMonthStatusLoading && !currentMonthStatusUnavailable && getCurrentMonthPaymentState(item) !== paymentFilter) {
      return false;
    }

    const query = q.trim().toLowerCase();
    if (!query) {
      return true;
    }

    const haystack = [item.fullName, item.email, item.phone, item.whatsAppNumber]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(query);
  }, [currentMonthStatusLoading, currentMonthStatusUnavailable, enrollmentSourceFilter, getCurrentMonthPaymentState, mode, paymentFilter, q, status]);

  const applyEnrollmentPatch = React.useCallback((enrollmentId: number, patch: Partial<NityaSevaEnrollment>) => {
    setItems((prev) => {
      const index = prev.findIndex((item) => item.id === enrollmentId);
      if (index === -1) {
        return prev;
      }

      const nextItem: NityaSevaEnrollment = {
        ...prev[index],
        ...patch,
      };

      const next = [...prev];
      if (matchesActiveFilters(nextItem)) {
        next[index] = nextItem;
        return next;
      }

      next.splice(index, 1);
      return next;
    });
  }, [matchesActiveFilters]);

  const maybeActivateEnrollmentIfNoDue = React.useCallback(async (enrollmentId: number) => {
    try {
      const detail = await fetchNityaSevaEnrollmentDetail(enrollmentId);
      const history = Array.isArray(detail?.donationHistory) ? detail.donationHistory : [];
      const hasDue = history.some((row) => row?.kind === 'due');
      const hasStartedDonating = history.some((row) => row?.kind !== 'due' && (row?.donation?.id || Number(row?.amount || 0) > 0));

      if (hasStartedDonating && !hasDue) {
        await updateNityaSevaEnrollment(enrollmentId, { status: 'active' });
        applyEnrollmentPatch(enrollmentId, {
          status: 'active',
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (_err) {
      // Keep donation flow resilient if status promotion check fails.
    }
  }, [applyEnrollmentPatch]);

  const refreshCurrentViewAfterMutation = React.useCallback(async () => {
    if (viewMode === 'months') {
      await Promise.all([loadEnrollments(), loadMonthSummary(), loadCurrentMonthStatuses()]);
    } else {
      await Promise.all([loadEnrollments(), loadCurrentMonthStatuses()]);
    }

    const followUps = [700, 1500, 2800];
    followUps.forEach((delayMs) => {
      window.setTimeout(() => {
        if (viewMode === 'months') {
          void loadEnrollments();
          void loadMonthSummary();
          void loadCurrentMonthStatuses();
          return;
        }
        void loadEnrollments();
        void loadCurrentMonthStatuses();
      }, delayMs);
    });
  }, [loadCurrentMonthStatuses, loadEnrollments, loadMonthSummary, viewMode]);

  const openDetail = React.useCallback((item: NityaSevaEnrollment) => {
    navigate(`/nitya-seva/details?id=${item.id}`);
  }, [navigate]);

  const openMonthDetail = React.useCallback((month: string) => {
    navigate(`/nitya-seva/months/details?month=${month}`);
  }, [navigate]);

  const openOfflineModal = React.useCallback(() => {
    setOfflineForm({ ...defaultOfflineEnrollmentForm, startDate: dayjs().format('YYYY-MM-DD') });
    setOfflineLookup(null);
    setPhoneSuggestions([]);
    setPhoneSuggestionError('');
    setOfflineFieldErrors({});
    setOfflineError('');
    setOfflineOpen(true);
  }, []);

  const closeOfflineModal = React.useCallback(() => {
    if (offlineSaving) {
      return;
    }
    setOfflineOpen(false);
    setOfflineLookup(null);
    setPhoneSuggestions([]);
    setPhoneSuggestionError('');
    setOfflineFieldErrors({});
    setOfflineError('');
  }, [offlineSaving]);

  React.useEffect(() => {
    if (!offlineOpen) {
      return;
    }
    const localPhone = toLocalPhone(offlineForm.phone);
    if (localPhone.length < 3) {
      setPhoneSuggestions([]);
      setPhoneSuggestionLoading(false);
      setPhoneSuggestionError('');
      return;
    }

    // Set loading immediately so the "No user matched" alert doesn't flash
    // during the 220 ms debounce window before the API call actually starts.
    setPhoneSuggestionLoading(true);
    setPhoneSuggestionError('');

    const timer = window.setTimeout(async () => {
      try {
        const users = await searchNityaSevaUsersByPhone(localPhone, 8);
        setPhoneSuggestions(Array.isArray(users) ? users : []);
        setPhoneSuggestionError('');
      } catch (_err) {
        setPhoneSuggestions([]);
        setPhoneSuggestionError('Could not search users. Check your connection and try again.');
      } finally {
        setPhoneSuggestionLoading(false);
      }
    }, 220);

    return () => {
      window.clearTimeout(timer);
    };
  }, [offlineForm.phone, offlineOpen]);

  const applySuggestion = React.useCallback((user: NityaSevaSearchUserItem) => {
    setOfflineForm((prev) => ({
      ...prev,
      fullName: prev.fullName || user.legalName || '',
      phone: toLocalPhone(user.phone || user.whatsAppNumber || prev.phone),
      whatsAppNumber: prev.whatsAppNumber || toLocalPhone(user.whatsAppNumber || user.phone || ''),
      email: prev.email || user.email || '',
      address: prev.address || user.address || '',
      state: prev.state || user.state || '',
      city: prev.city || String(user.city || '').toUpperCase(),
      pincode: prev.pincode || user.pincode || '',
      country: prev.country || user.country || 'India',
      dateOfBirth: prev.dateOfBirth || user.dateOfBirth || '',
      anniversaryDate: prev.anniversaryDate || normalizeDateInput(user.anniversaryDate) || '',
      panNumber: prev.panNumber || String(user.panNumber || '').toUpperCase(),
    }));
    setOfflineFieldErrors((prev) => ({ ...prev, phone: '', email: '', address: '', state: '', city: '', pincode: '', dateOfBirth: '', panNumber: '' }));
    setOfflineLookup((prev) => ({
      found: true,
      ambiguous: false,
      matchedBy: prev?.matchedBy || 'phone',
      user: {
        id: user.id,
        legalName: user.legalName,
        phone: user.phone,
        whatsAppNumber: user.whatsAppNumber,
        email: user.email,
        address: user.address,
        city: user.city,
        state: user.state,
        country: user.country,
        pincode: user.pincode,
        panNumber: user.panNumber,
        dateOfBirth: user.dateOfBirth,
        anniversaryDate: user.anniversaryDate,
      },
      existingEnrollment: user.hasEnrollment
        ? {
            id: user.enrollmentId || 0,
            userId: user.id,
            fullName: user.legalName || '',
            whatsAppNumber: user.whatsAppNumber || user.phone || '',
            phone: user.phone || '',
            email: user.email || '',
            address: '',
            dateOfBirth: '',
            pledgeTier: '',
            pledgeAmount: 0,
            paymentFrequency: 'monthly',
            submissionMode: 'cash_first_sunday',
            status: user.enrollmentStatus || 'pending',
            createdAt: '',
            updatedAt: '',
          }
        : undefined,
    }));
    setOfflineError('');
  }, []);

  React.useEffect(() => {
    const localPhone = toLocalPhone(offlineForm.phone);
    if (localPhone.length !== 10) {
      lastAutoSelectedRef.current = '';
      return;
    }
    if (!phoneSuggestions.length) {
      return;
    }

    const exactMatches = phoneSuggestions.filter((user) => {
      const primary = toLocalPhone(user.phone || '');
      const secondary = toLocalPhone(user.whatsAppNumber || '');
      return primary === localPhone || secondary === localPhone;
    });

    if (exactMatches.length !== 1) {
      return;
    }

    const [match] = exactMatches;
    const selectionKey = `${localPhone}:${match.id}`;
    if (lastAutoSelectedRef.current === selectionKey) {
      return;
    }

    if (offlineLookup?.user?.id && Number(offlineLookup.user.id) === match.id) {
      lastAutoSelectedRef.current = selectionKey;
      return;
    }

    applySuggestion(match);
    lastAutoSelectedRef.current = selectionKey;
  }, [applySuggestion, offlineForm.phone, offlineLookup?.user?.id, phoneSuggestions]);

  const saveOfflineEnrollment = React.useCallback(async () => {
    const lockedPhone = toLocalPhone(offlineLookup?.user?.phone || offlineLookup?.user?.whatsAppNumber || '');
    const localPhone = hasSelectedExistingUser && lockedPhone ? lockedPhone : toLocalPhone(offlineForm.phone);
    const localWhatsApp = toLocalPhone(offlineForm.whatsAppNumber || offlineForm.phone);
    const email = existingEmailLocked ? String(offlineLookup?.user?.email || '').trim() : offlineForm.email.trim();
    const city = offlineForm.city.trim().toUpperCase();
    const state = offlineForm.state.trim();
    const pincode = offlineForm.pincode.trim();
    const address = offlineForm.address.trim();
    const pledgeAmount = Number(offlineForm.pledgeAmount || 0);
    const pledgeTier = getPledgeTierFromAmount(pledgeAmount);
    const paymentFrequency = offlineForm.paymentFrequency || 'monthly';
    const submissionMode = offlineForm.submissionMode === 'auto_razorpay' ? 'online_transfer' : offlineForm.submissionMode;

    const fieldErrors: Record<string, string> = {};
    if (!offlineForm.fullName.trim()) fieldErrors.fullName = 'Full name is required.';
    if (localPhone.length !== 10) fieldErrors.phone = 'Phone must be exactly 10 digits.';
    if (localWhatsApp.length !== 10) fieldErrors.whatsAppNumber = 'WhatsApp number must be exactly 10 digits.';
    if (email && !emailRegex.test(email)) {
      fieldErrors.email = 'Enter a valid email address.';
    }
    if (!address) {
      fieldErrors.address = 'Detailed address is required.';
    } else if (address.length > 100 || !addressRegex.test(address)) {
      fieldErrors.address = "Max 100 characters. Only letters, numbers, '.', ',', '-', and '/' allowed.";
    }
    if (!state) fieldErrors.state = 'State is required.';
    if (!city) {
      fieldErrors.city = 'City is required.';
    } else if (city.length > 20 || !cityRegex.test(city)) {
      fieldErrors.city = 'Max 20 characters. Only uppercase letters and spaces allowed.';
    }
    if (!/^\d{6}$/.test(pincode)) fieldErrors.pincode = 'Pincode must be exactly 6 digits.';
    if (!offlineForm.startDate.trim()) fieldErrors.startDate = 'Start date is required.';
    if (offlineForm.panNumber && !panRegex.test(offlineForm.panNumber.trim().toUpperCase())) {
      fieldErrors.panNumber = 'PAN must be exactly 10 characters: 5 letters, 4 digits, 1 letter.';
    }
    if (pledgeAmount <= 0) {
      fieldErrors.pledgeAmount = 'Please enter a valid pledge amount.';
    }
    setOfflineFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setOfflineError(Object.values(fieldErrors)[0]);
      return;
    }
    if (offlineLookup?.existingEnrollment) {
      setOfflineError('Nitya Seva enrollment already exists for this user.');
      return;
    }

    setOfflineError('');
    setOfflineSaving(true);
    try {
      await createOfflineNityaSevaEnrollment({
        userId: hasSelectedExistingUser ? Number(offlineLookup?.user?.id || 0) : undefined,
        createUserIfMissing: true,
        fullName: offlineForm.fullName.trim(),
        phone: localPhone,
        whatsAppNumber: localWhatsApp,
        email,
        address,
        city,
        state,
        pincode,
        country: 'India',
        dateOfBirth: offlineForm.dateOfBirth || undefined,
        anniversaryDate: offlineForm.anniversaryDate || undefined,
        startDate: offlineForm.startDate || undefined,
        pledgeTier,
        pledgeAmount,
        paymentFrequency,
        submissionMode,
        panNumber: offlineForm.panNumber.trim().toUpperCase(),
        status: offlineForm.status.trim(),
        adminNote: offlineForm.adminNote.trim(),
      });
      setOfflineOpen(false);
      setOfflineLookup(null);
      setOfflineForm(defaultOfflineEnrollmentForm);
      await refreshCurrentViewAfterMutation();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create offline enrollment.';
      setOfflineError(message);
    } finally {
      setOfflineSaving(false);
    }
  }, [existingEmailLocked, hasSelectedExistingUser, offlineForm, offlineLookup, refreshCurrentViewAfterMutation]);

  const filteredSortedItems = React.useMemo(() => {
    const next = [...items].filter(matchesActiveFilters);
    next.sort((a, b) => {
      let result = 0;
      switch (enrollmentSort.key) {
        case 'fullName':
          result = compareText(a.fullName, b.fullName);
          break;
        case 'whatsAppNumber':
          result = compareText(a.whatsAppNumber, b.whatsAppNumber);
          break;
        case 'phone':
          result = compareText(toLocalPhone(a.phone || a.whatsAppNumber || ''), toLocalPhone(b.phone || b.whatsAppNumber || ''));
          break;
        case 'email':
          result = compareText(a.email, b.email);
          break;
        case 'submissionMode':
          result = compareText(modeLabel[a.submissionMode] || a.submissionMode, modeLabel[b.submissionMode] || b.submissionMode);
          break;
        case 'pledgeAmount':
          result = compareNumbers(a.pledgeAmount, b.pledgeAmount);
          break;
        case 'status':
          result = compareText(a.status, b.status);
          break;
        case 'currentMonth': {
          const paymentRank = (item: NityaSevaEnrollment) => {
            const paymentState = getCurrentMonthPaymentState(item);
            if (paymentState === 'paid') return 2;
            if (paymentState === 'unpaid') return 1;
            return 0;
          };
          result = compareNumbers(paymentRank(a), paymentRank(b));
          break;
        }
        case 'lastManualDonationAt':
          result = compareDates(a.lastManualDonationAt || a.createdAt, b.lastManualDonationAt || b.createdAt);
          break;
        default:
          result = 0;
      }
      return applySortDirection(result, enrollmentSort.direction);
    });
    return next;
  }, [enrollmentSort, getCurrentMonthPaymentState, items, matchesActiveFilters]);

  const filteredSortedMonthItems = React.useMemo(() => {
    const next = [...monthItems].filter((item) => {
      if (paymentFilter === 'paid') {
        return item.paidCount > 0;
      }
      if (paymentFilter === 'unpaid') {
        return item.dueCount > 0;
      }
      return true;
    });

    next.sort((a, b) => {
      let result = 0;
      switch (monthSort.key) {
        case 'month':
          result = compareDates(a.month, b.month);
          break;
        case 'paidCount':
          result = compareNumbers(a.paidCount, b.paidCount);
          break;
        case 'dueCount':
          result = compareNumbers(a.dueCount, b.dueCount);
          break;
        case 'totalEnrollments':
          result = compareNumbers(a.totalEnrollments, b.totalEnrollments);
          break;
        case 'manualCount':
          result = compareNumbers(a.manualCount, b.manualCount);
          break;
        case 'subscriptionCount':
          result = compareNumbers(a.subscriptionCount, b.subscriptionCount);
          break;
        case 'totalExpected':
          result = compareNumbers(a.totalExpected, b.totalExpected);
          break;
        case 'totalCollected':
          result = compareNumbers(a.totalCollected, b.totalCollected);
          break;
        case 'pendingAmount':
          result = compareNumbers(a.pendingAmount, b.pendingAmount);
          break;
        default:
          result = 0;
      }
      return applySortDirection(result, monthSort.direction);
    });

    return next;
  }, [monthItems, monthSort, paymentFilter]);

  const enrollmentExportColumns: ExportColumn[] = React.useMemo(() => [
    { key: 'name', label: 'Name' },
    { key: 'whatsApp', label: 'WhatsApp' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'mode', label: 'Submission Mode' },
    { key: 'enrollmentSource', label: 'Enrollment Source' },
    { key: 'pledgeAmount', label: 'Pledge Amount (₹)' },
    { key: 'pledgeTier', label: 'Pledge Tier' },
    { key: 'paymentFrequency', label: 'Payment Frequency' },
    { key: 'status', label: 'Status' },
    { key: 'currentMonth', label: 'Current Month' },
    { key: 'lastDonation', label: 'Last Donation' },
    { key: 'startedDate', label: 'Started Date' },
  ], []);

  const enrollmentExportRows = React.useMemo(() => filteredSortedItems.map((item) => ({
    name: item.fullName || '',
    whatsApp: item.whatsAppNumber || '',
    phone: toLocalPhone(item.phone || item.whatsAppNumber || '') || '',
    email: item.email || '',
    mode: modeLabel[item.submissionMode] || item.submissionMode,
    enrollmentSource: getEnrollmentSourceLabel(item.enrollmentSource),
    pledgeAmount: Number(item.pledgeAmount || 0).toFixed(0),
    pledgeTier: item.pledgeTier || '',
    paymentFrequency: getPaymentFrequencyLabel(item.paymentFrequency || 'monthly'),
    status: item.status || '',
    currentMonth: currentMonthStatusByEnrollmentId[item.id]?.label || (item.status === 'paused' ? 'Paused' : ''),
    lastDonation: item.lastManualDonationAt ? dayjs(item.lastManualDonationAt).format('D MMM YYYY') : '',
    startedDate: item.createdAt ? dayjs(item.createdAt).format('D MMM YYYY') : '',
  })), [filteredSortedItems, currentMonthStatusByEnrollmentId]);

  const monthExportColumns: ExportColumn[] = React.useMemo(() => [
    { key: 'month', label: 'Month' },
    { key: 'paid', label: 'Paid' },
    { key: 'unpaid', label: 'Unpaid' },
    { key: 'totalEnrollments', label: 'Total Enrollments' },
    { key: 'manual', label: 'Manual' },
    { key: 'subscription', label: 'Subscription' },
    { key: 'expected', label: 'Expected (₹)' },
    { key: 'collected', label: 'Collected (₹)' },
    { key: 'pending', label: 'Pending (₹)' },
  ], []);

  const monthExportRows = React.useMemo(() => filteredSortedMonthItems.map((item) => ({
    month: dayjs(item.month).format('MMM YYYY'),
    paid: item.paidCount,
    unpaid: item.dueCount,
    totalEnrollments: item.totalEnrollments,
    manual: item.manualCount,
    subscription: item.subscriptionCount,
    expected: Number(item.totalExpected || 0).toFixed(0),
    collected: Number(item.totalCollected || 0).toFixed(0),
    pending: Number(item.pendingAmount || 0).toFixed(0),
  })), [filteredSortedMonthItems]);

  const activeCount = filteredSortedItems.filter((item) => item.status === 'active').length;
  const pausedCount = filteredSortedItems.filter((item) => item.status === 'paused').length;
  const needsFollowUpCount = filteredSortedItems.filter((item) => item.status === 'needs_follow_up').length;

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: { xs: 'calc(12px + var(--Header-height))', sm: 'calc(12px + var(--Header-height))', md: 3 },
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          gap: 1,
          bgcolor: 'background.body',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PageBreadcrumbs links={[]} current="Nitya Seva" />
        </Box>

        <Stack spacing={0.75}>
          <Typography level="h1" sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, lineHeight: 1.05 }}>
            Nitya Seva Management
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.tertiary', maxWidth: 760 }}>
            Track enrollments, review pledge modes, and record monthly Nitya Seva donations from one place.
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Chip variant="soft" color="success">{activeCount} active</Chip>
          <Chip variant="soft" color="warning">{needsFollowUpCount} needs follow up</Chip>
          <Chip variant="soft" color="neutral">{pausedCount} paused</Chip>
          <Chip variant="soft" color="primary">{filteredSortedItems.length} total enrollments</Chip>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button sx={{ width: { xs: '100%', sm: 'auto' } }} variant={viewMode === 'enrollments' ? 'solid' : 'soft'} color={viewMode === 'enrollments' ? 'primary' : 'neutral'} onClick={() => setViewMode('enrollments')}>
            Enrollment View
          </Button>
          <Button sx={{ width: { xs: '100%', sm: 'auto' } }} variant={viewMode === 'months' ? 'solid' : 'soft'} color={viewMode === 'months' ? 'primary' : 'neutral'} startDecorator={<CalendarMonthRounded fontSize="small" />} onClick={() => setViewMode('months')}>
            Month-wise View
          </Button>
          <Button sx={{ width: { xs: '100%', sm: 'auto' } }} variant="soft" color="success" startDecorator={<PersonAddAltRounded fontSize="small" />} onClick={openOfflineModal}>
            Add New Member
          </Button>
        </Stack>

        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.25}
              alignItems={{ xs: 'stretch', lg: 'end' }}
              sx={{ flexWrap: 'wrap' }}
            >
              <FormControl sx={{ flex: { lg: '1 1 260px' }, width: '100%', minWidth: { xs: '100%', sm: 240 } }}>
                <FormLabel>Search</FormLabel>
                <Input
                  placeholder="Search name, email, or phone"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  startDecorator={<SearchRounded fontSize="small" />}
                />
              </FormControl>
              <FormControl sx={{ flex: { lg: '1 1 180px' }, width: '100%', minWidth: { xs: '100%', sm: 180 } }}>
                <FormLabel>Status</FormLabel>
                <Select value={status} onChange={(_, val) => setStatus((val as string) || '')} placeholder="Status">
                  <Option value="">All Status</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="active">Active</Option>
                  <Option value="paused">Paused</Option>
                  <Option value="needs_follow_up">Needs Follow Up</Option>
                </Select>
              </FormControl>
              <FormControl sx={{ flex: { lg: '1 1 200px' }, width: '100%', minWidth: { xs: '100%', sm: 190 } }}>
                <FormLabel>Submission Mode</FormLabel>
                <Select value={mode} onChange={(_, val) => setMode((val as string) || '')} placeholder="Mode">
                  <Option value="">All Modes</Option>
                  <Option value="ecs_nach">ECS / NACH</Option>
                  <Option value="auto_razorpay">Auto Razorpay</Option>
                  <Option value="online_transfer">Online Transfer</Option>
                  <Option value="cash_first_sunday">Cash 1st Sunday</Option>
                </Select>
              </FormControl>
              {viewMode === 'enrollments' ? (
                <FormControl sx={{ flex: { lg: '1 1 180px' }, width: '100%', minWidth: { xs: '100%', sm: 180 } }}>
                  <FormLabel>Enrollment</FormLabel>
                  <Select value={enrollmentSourceFilter} onChange={(_, val) => setEnrollmentSourceFilter((val as string) || '')} placeholder="Enrollment source">
                    <Option value="">All Enrollments</Option>
                    <Option value="online">Online</Option>
                    <Option value="offline">Offline</Option>
                  </Select>
                </FormControl>
              ) : null}
              <FormControl sx={{ flex: { lg: '1 1 170px' }, width: '100%', minWidth: { xs: '100%', sm: 170 } }}>
                <FormLabel>{viewMode === 'months' ? 'Donation' : 'Current Month'}</FormLabel>
                <Select value={paymentFilter} onChange={(_, val) => setPaymentFilter((val as string) || '')} placeholder="Payment status">
                  <Option value="">All</Option>
                  <Option value="paid">Paid</Option>
                  <Option value="unpaid">Unpaid</Option>
                </Select>
              </FormControl>
              <Button onClick={() => { if (viewMode === 'months') { loadMonthSummary(); } else { loadEnrollments(); loadCurrentMonthStatuses(); } }} loading={viewMode === 'months' ? monthLoading : loading} startDecorator={!(viewMode === 'months' ? monthLoading : loading) ? <RefreshRounded fontSize="small" /> : undefined} sx={{ minWidth: 132, width: { xs: '100%', lg: 'auto' }, flexShrink: 0 }}>
                Refresh
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {error ? <Alert color="danger">{error}</Alert> : null}

        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
              <Box>
                <Typography level="title-md">
                  {viewMode === 'enrollments' ? 'Enrollment Overview' : 'Month-wise Overview'}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  {viewMode === 'enrollments'
                    ? 'Review donor-level enrollment information and record monthly donations.'
                    : 'See one row per Nitya Seva month, then drill down into all enrollments for that month.'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                {viewMode === 'months' ? (
                  <Chip variant="soft" color="primary">{filteredSortedMonthItems.length} months</Chip>
                ) : null}
                <Button
                  size="sm"
                  variant="soft"
                  color="neutral"
                  startDecorator={<FileDownloadRounded fontSize="small" />}
                  onClick={() => setExportOpen(true)}
                  disabled={viewMode === 'enrollments' ? !filteredSortedItems.length : !filteredSortedMonthItems.length}
                >
                  Export
                </Button>
              </Stack>
            </Stack>
            {viewMode === 'months' && monthError ? <Alert color="warning" sx={{ mb: 1.5 }}>{monthError}</Alert> : null}
            <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'hidden', borderColor: 'divider', backgroundColor: 'background.surface' }}>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                {viewMode === 'enrollments' ? (
                <Table
                  stickyHeader
                  hoverRow
                  sx={{
                    '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                    '--Table-headerUnderlineThickness': '1px',
                    '--TableCell-paddingY': '14px',
                    '--TableCell-paddingX': '14px',
                    tableLayout: 'fixed',
                    minWidth: 1100,
                    '& thead th': {
                      textTransform: 'uppercase',
                      fontSize: '0.72rem',
                      letterSpacing: 0.6,
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                    },
                    '& thead th:last-child': {
                      position: 'sticky',
                      right: 0,
                      top: 0,
                      zIndex: 30,
                      backgroundColor: 'var(--joy-palette-background-level1)',
                      boxShadow: '-1px 0 0 var(--joy-palette-divider)',
                      textAlign: 'center',
                      width: '144px',
                      minWidth: '144px',
                      maxWidth: '144px',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                    },
                    '& tbody tr:nth-of-type(even)': {
                      backgroundColor: 'var(--joy-palette-background-level1)',
                    },
                    '& tbody tr:hover': {
                      backgroundColor: 'var(--joy-palette-background-level2)',
                    },
                    '& tbody td': {
                      verticalAlign: 'middle',
                    },
                    '& tbody td:last-child': {
                      position: 'sticky',
                      right: 0,
                      zIndex: 20,
                      backgroundColor: 'background.surface',
                      boxShadow: '-1px 0 0 var(--joy-palette-divider)',
                      textAlign: 'center',
                      width: '144px',
                      minWidth: '144px',
                      maxWidth: '144px',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                    },
                    '& tbody tr:nth-of-type(even) td:last-child': {
                      backgroundColor: 'var(--joy-palette-background-level1)',
                    },
                    '& tbody tr:hover td:last-child': {
                      backgroundColor: 'var(--joy-palette-background-level2)',
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: '220px' }} />
                    <col style={{ width: '180px' }} />
                    <col style={{ width: '170px' }} />
                    <col style={{ width: '220px' }} />
                    <col style={{ width: '180px' }} />
                    <col style={{ width: '220px' }} />
                    <col style={{ width: '130px' }} />
                    <col style={{ width: '165px' }} />
                    <col style={{ width: '170px' }} />
                    <col style={{ width: '144px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{renderSortableHeader('Name', enrollmentSort.key === 'fullName', enrollmentSort.direction, () => handleEnrollmentSort('fullName'))}</th>
                      <th>{renderSortableHeader('WhatsApp', enrollmentSort.key === 'whatsAppNumber', enrollmentSort.direction, () => handleEnrollmentSort('whatsAppNumber'))}</th>
                      <th>{renderSortableHeader('Phone', enrollmentSort.key === 'phone', enrollmentSort.direction, () => handleEnrollmentSort('phone'))}</th>
                      <th>{renderSortableHeader('Email', enrollmentSort.key === 'email', enrollmentSort.direction, () => handleEnrollmentSort('email'))}</th>
                      <th>{renderSortableHeader('Mode', enrollmentSort.key === 'submissionMode', enrollmentSort.direction, () => handleEnrollmentSort('submissionMode'))}</th>
                      <th>{renderSortableHeader('Pledge', enrollmentSort.key === 'pledgeAmount', enrollmentSort.direction, () => handleEnrollmentSort('pledgeAmount'))}</th>
                      <th>{renderSortableHeader('Status', enrollmentSort.key === 'status', enrollmentSort.direction, () => handleEnrollmentSort('status'))}</th>
                      <th>{renderSortableHeader('Current Month', enrollmentSort.key === 'currentMonth', enrollmentSort.direction, () => handleEnrollmentSort('currentMonth'))}</th>
                      <th>{renderSortableHeader('Last Donation', enrollmentSort.key === 'lastManualDonationAt', enrollmentSort.direction, () => handleEnrollmentSort('lastManualDonationAt'))}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && !filteredSortedItems.length ? (
                      <tr>
                        <td colSpan={10}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                            <CircularProgress size="sm" />
                            <Typography level="body-sm">Loading enrollments...</Typography>
                          </Stack>
                        </td>
                      </tr>
                    ) : filteredSortedItems.length ? filteredSortedItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <Button
                            variant="plain"
                            color="neutral"
                            sx={{ p: 0, fontWeight: 700, justifyContent: 'flex-start', fontSize: '1.05rem' }}
                            onClick={() => openDetail(item)}
                          >
                            {item.fullName}
                          </Button>
                        </td>
                        <td>{item.whatsAppNumber || '—'}</td>
                        <td>{toLocalPhone(item.phone || item.whatsAppNumber || '') || '—'}</td>
                        <td>
                          <Typography level="body-sm" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.email || '—'}>
                            {item.email || '—'}
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-sm">{modeLabel[item.submissionMode] || item.submissionMode}</Typography>
                          <Chip size="sm" variant="soft" color={getEnrollmentSourceColor(item.enrollmentSource)} sx={{ mt: 0.5 }}>
                            {getEnrollmentSourceLabel(item.enrollmentSource)}
                          </Chip>
                        </td>
                        <td>
                          <Typography level="body-sm">₹{item.pledgeAmount.toFixed(0)}</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{item.pledgeTier} · {getPaymentFrequencyLabel(item.paymentFrequency || 'monthly')}</Typography>
                        </td>
                        <td><Chip size="sm" variant="soft" color={item.status === 'active' ? 'success' : item.status === 'needs_follow_up' ? 'warning' : 'neutral'}>{item.status}</Chip></td>
                        <td>
                          {(() => {
                            const fallback = item.status === 'paused'
                              ? { label: 'Paused', color: 'neutral' as const }
                              : currentMonthStatusLoading
                                ? { label: 'Checking...', color: 'neutral' as const }
                                : currentMonthStatusUnavailable
                                  ? { label: 'Unavailable', color: 'neutral' as const }
                                  : { label: 'Not due yet', color: 'neutral' as const };
                            const chip = currentMonthPaymentChip(currentMonthStatusByEnrollmentId[item.id] || fallback);
                            return <Chip size="sm" variant="soft" color={chip.color}>{chip.label}</Chip>;
                          })()}
                        </td>
                        <td>
                          <Typography level="body-sm">{item.lastManualDonationAt ? dayjs(item.lastManualDonationAt).format('D MMM YYYY') : '—'}</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Started: {item.createdAt ? dayjs(item.createdAt).format('D MMM YYYY') : '—'}</Typography>
                        </td>
                        <td>
                          <Stack direction="row" spacing={0.75} justifyContent="center" alignItems="center" sx={{ width: '100%', flexWrap: 'nowrap' }}>
                            <Tooltip title="View">
                              <IconButton size="sm" variant="soft" color="neutral" onClick={() => openDetail(item)}>
                                <VisibilityRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="sm" variant="soft" onClick={() => {
                                setEditTarget(item);
                                setEditForm({
                                  fullName: item.fullName || '',
                                  phone: toLocalPhone(item.phone || item.whatsAppNumber || ''),
                                  whatsAppNumber: toLocalPhone(item.whatsAppNumber || item.phone || ''),
                                  email: item.email || '',
                                  address: item.address || '',
                                  dateOfBirth: normalizeDateInput(item.dateOfBirth),
                                  anniversaryDate: normalizeDateInput(item.anniversaryDate),
                                  startDate: normalizeDateInput(item.createdAt),
                                  pledgeAmount: Number(item.pledgeAmount || 0),
                                  paymentFrequency: item.paymentFrequency || 'monthly',
                                  submissionMode: item.submissionMode,
                                  panNumber: item.panNumber || '',
                                  status: item.status || 'pending',
                                  adminNote: item.adminNote || '',
                                });
                              }}>
                                <EditRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Record Donation">
                              <IconButton
                                size="sm"
                                variant="soft"
                                color="primary"
                                onClick={() => {
                                  const defaultFrequency = item.paymentFrequency || 'monthly';
                                  setManualTarget(item);
                                  setManualPaymentFrequency(defaultFrequency);
                                  setManualAmount(getCycleAmount(Number(item.pledgeAmount || 0), defaultFrequency).toFixed(0));
                                  setManualMethod(item.submissionMode === 'cash_first_sunday' ? 'Cash' : 'Online');
                                  setManualDate(dayjs().format('YYYY-MM-DD'));
                                  setManualForMonth(dayjs().format('YYYY-MM'));
                                  setManualTransactionId('');
                                  setManualNote('');
                                }}
                              >
                                <FavoriteRounded fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={10}>
                          <Stack spacing={0.5} sx={{ py: 2, alignItems: 'center' }}>
                            <Typography level="title-sm">No Nitya Seva enrollments found</Typography>
                            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                              Adjust the filters or search query and try again.
                            </Typography>
                          </Stack>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                ) : (
                <Table
                  stickyHeader
                  hoverRow
                  sx={{
                    '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                    '--Table-headerUnderlineThickness': '1px',
                    '--TableCell-paddingY': '14px',
                    '--TableCell-paddingX': '14px',
                    tableLayout: 'fixed',
                    minWidth: 1200,
                    '& thead th': {
                      textTransform: 'uppercase',
                      fontSize: '0.72rem',
                      letterSpacing: 0.6,
                      color: 'text.secondary',
                      whiteSpace: 'nowrap',
                    },
                    '& thead th:last-child': {
                      position: 'sticky',
                      right: 0,
                      top: 0,
                      zIndex: 30,
                      backgroundColor: 'var(--joy-palette-background-level1)',
                      boxShadow: '-1px 0 0 var(--joy-palette-divider)',
                      textAlign: 'center',
                      width: '88px',
                      minWidth: '88px',
                      maxWidth: '88px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                    },
                    '& tbody tr:nth-of-type(even)': {
                      backgroundColor: 'var(--joy-palette-background-level1)',
                    },
                    '& tbody tr:hover': {
                      backgroundColor: 'var(--joy-palette-background-level2)',
                    },
                    '& tbody td': {
                      verticalAlign: 'middle',
                    },
                    '& tbody td:last-child': {
                      position: 'sticky',
                      right: 0,
                      zIndex: 20,
                      backgroundColor: 'background.surface',
                      boxShadow: '-1px 0 0 var(--joy-palette-divider)',
                      textAlign: 'center',
                      width: '88px',
                      minWidth: '88px',
                      maxWidth: '88px',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                    },
                    '& tbody tr:nth-of-type(even) td:last-child': {
                      backgroundColor: 'var(--joy-palette-background-level1)',
                    },
                    '& tbody tr:hover td:last-child': {
                      backgroundColor: 'var(--joy-palette-background-level2)',
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '120px' }} />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '160px' }} />
                    <col style={{ width: '160px' }} />
                    <col style={{ width: '150px' }} />
                    <col style={{ width: '150px' }} />
                    <col style={{ width: '88px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{renderSortableHeader('Month', monthSort.key === 'month', monthSort.direction, () => handleMonthSort('month'))}</th>
                      <th>{renderSortableHeader('Paid', monthSort.key === 'paidCount', monthSort.direction, () => handleMonthSort('paidCount'))}</th>
                      <th>{renderSortableHeader('Unpaid', monthSort.key === 'dueCount', monthSort.direction, () => handleMonthSort('dueCount'))}</th>
                      <th>{renderSortableHeader('Total Enrollments', monthSort.key === 'totalEnrollments', monthSort.direction, () => handleMonthSort('totalEnrollments'))}</th>
                      <th>{renderSortableHeader('Manual', monthSort.key === 'manualCount', monthSort.direction, () => handleMonthSort('manualCount'))}</th>
                      <th>{renderSortableHeader('Subscription', monthSort.key === 'subscriptionCount', monthSort.direction, () => handleMonthSort('subscriptionCount'))}</th>
                      <th>{renderSortableHeader('Expected', monthSort.key === 'totalExpected', monthSort.direction, () => handleMonthSort('totalExpected'))}</th>
                      <th>{renderSortableHeader('Collected', monthSort.key === 'totalCollected', monthSort.direction, () => handleMonthSort('totalCollected'))}</th>
                      <th>{renderSortableHeader('Pending Amount', monthSort.key === 'pendingAmount', monthSort.direction, () => handleMonthSort('pendingAmount'))}</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthLoading && !filteredSortedMonthItems.length ? (
                      <tr>
                        <td colSpan={10}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                            <CircularProgress size="sm" />
                            <Typography level="body-sm">Loading month summary...</Typography>
                          </Stack>
                        </td>
                      </tr>
                    ) : filteredSortedMonthItems.length ? filteredSortedMonthItems.map((item) => {
                      const monthKey = dayjs(item.month).format('YYYY-MM');
                      return (
                        <tr key={monthKey}>
                          <td>{dayjs(item.month).format('MMM YYYY')}</td>
                          <td><Chip size="sm" variant="soft" color="success">{item.paidCount} paid</Chip></td>
                          <td><Chip size="sm" variant="soft" color={item.dueCount > 0 ? 'danger' : 'neutral'}>{item.dueCount} unpaid</Chip></td>
                          <td>{item.totalEnrollments}</td>
                          <td>{item.manualCount}</td>
                          <td>{item.subscriptionCount}</td>
                          <td>₹{item.totalExpected.toFixed(0)}</td>
                          <td>₹{item.totalCollected.toFixed(0)}</td>
                          <td>₹{item.pendingAmount.toFixed(0)}</td>
                          <td>
                            <Tooltip title="View month details">
                              <span>
                                <IconButton size="sm" variant="soft" color="neutral" onClick={() => openMonthDetail(monthKey)} disabled={!monthApiAvailable}>
                                  <VisibilityRounded fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={10}>
                          <Stack spacing={0.5} sx={{ py: 2, alignItems: 'center' }}>
                            <Typography level="title-sm">{monthApiAvailable ? 'No month summary available' : 'Month-wise view unavailable'}</Typography>
                            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                              {monthApiAvailable ? 'Adjust the filters or search query and try again.' : 'Restart the backend with the new month-wise routes to enable this view.'}
                            </Typography>
                          </Stack>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                )}
              </Box>
            </Sheet>
          </CardContent>
        </Card>

        <Modal open={Boolean(editTarget)} onClose={() => { setEditTarget(null); setEditForm(null); }}>
          <ModalDialog
            sx={{
              width: 'min(96vw, 760px)',
              maxWidth: '760px',
              maxHeight: '92vh',
              overflowY: 'auto',
            }}
          >
            <ModalClose />
            <Typography level="title-lg">Edit Enrollment</Typography>
            {editForm ? (
              <Stack spacing={1.25}>
                <FormControl>
                  <FormLabel>Full Name *</FormLabel>
                  <Input value={editForm.fullName} onChange={(e) => setEditForm((prev) => (prev ? { ...prev, fullName: e.target.value } : prev))} />
                </FormControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Phone *</FormLabel>
                    <Input value={editForm.phone} onChange={(e) => setEditForm((prev) => (prev ? { ...prev, phone: toLocalPhone(e.target.value) } : prev))} />
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>WhatsApp *</FormLabel>
                    <Input value={editForm.whatsAppNumber} onChange={(e) => setEditForm((prev) => (prev ? { ...prev, whatsAppNumber: toLocalPhone(e.target.value) } : prev))} />
                  </FormControl>
                </Stack>
                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <Input value={editForm.email} onChange={(e) => setEditForm((prev) => (prev ? { ...prev, email: e.target.value } : prev))} />
                </FormControl>
                <FormControl>
                  <FormLabel>Address *</FormLabel>
                  <Textarea minRows={2} value={editForm.address} onChange={(e) => setEditForm((prev) => (prev ? { ...prev, address: e.target.value } : prev))} />
                </FormControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Date of Birth</FormLabel>
                    <Input
                      sx={datePickerInputSx}
                      type="date"
                      slotProps={{ input: { max: '9999-12-31' } }}
                      value={editForm.dateOfBirth}
                      onChange={(e) => setEditForm((prev) => (prev ? { ...prev, dateOfBirth: e.target.value } : prev))}
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Date of Anniversary (if applicable)</FormLabel>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                      <Input
                        sx={[datePickerInputSx, { flex: 1 }]}
                        type="date"
                        slotProps={{ input: { max: '9999-12-31' } }}
                        value={editForm.anniversaryDate}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, anniversaryDate: e.target.value } : prev))}
                      />
                      {editForm.anniversaryDate ? (
                        <Button
                          variant="soft"
                          color="danger"
                          onClick={() => setEditForm((prev) => (prev ? { ...prev, anniversaryDate: '' } : prev))}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </Stack>
                  </FormControl>
                </Stack>
                <FormControl>
                  <FormLabel>Nitya Seva Start Date</FormLabel>
                  <Input
                    sx={datePickerInputSx}
                    type="date"
                    slotProps={{ input: { max: '9999-12-31' } }}
                    value={editForm.startDate}
                    onChange={(e) => setEditForm((prev) => (prev ? { ...prev, startDate: e.target.value } : prev))}
                  />
                  <FormHelperText>This controls when dues begin for this enrollment.</FormHelperText>
                </FormControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Pledge Amount *</FormLabel>
                    <Input
                      type="number"
                      value={editForm.pledgeAmount || ''}
                      slotProps={{ input: { min: 1, step: 1 } }}
                      onChange={(e) => setEditForm((prev) => (prev ? { ...prev, pledgeAmount: parsePledgeAmount(e.target.value) } : prev))}
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Payment Frequency *</FormLabel>
                    <Select
                      value={editForm.paymentFrequency}
                      onChange={(_, val) => setEditForm((prev) => (prev ? { ...prev, paymentFrequency: (val as NityaSevaPaymentFrequency) || 'monthly' } : prev))}
                    >
                      {paymentFrequencyOptions.map((option) => (
                        <Option key={option.value} value={option.value}>{option.label}</Option>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <FormControl>
                  <FormLabel>Submission Mode *</FormLabel>
                  <RadioGroup
                    value={editForm.submissionMode}
                    onChange={(e) => setEditForm((prev) => (prev ? { ...prev, submissionMode: e.target.value as NityaSevaEnrollment['submissionMode'] } : prev))}
                  >
                    <Radio value="cash_first_sunday" label="Cash on first Sunday" />
                    <Radio value="online_transfer" label="Online transfer" />
                    <Radio value="ecs_nach" label="ECS / NACH" />
                  </RadioGroup>
                </FormControl>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>PAN Number (optional)</FormLabel>
                    <Input value={editForm.panNumber} onChange={(e) => setEditForm((prev) => (prev ? { ...prev, panNumber: e.target.value.toUpperCase() } : prev))} />
                  </FormControl>
                  <FormControl sx={{ flex: 1 }}>
                    <FormLabel>Status</FormLabel>
                    <Select value={editForm.status} onChange={(_, val) => setEditForm((prev) => (prev ? { ...prev, status: (val as string) || 'pending' } : prev))}>
                      <Option value="pending">Pending</Option>
                      <Option value="active">Active</Option>
                      <Option value="paused">Paused</Option>
                      <Option value="needs_follow_up">Needs Follow Up</Option>
                    </Select>
                  </FormControl>
                </Stack>
                <FormControl>
                  <FormLabel>Admin note</FormLabel>
                  <Textarea minRows={3} placeholder="Admin note" value={editForm.adminNote} onChange={(e) => setEditForm((prev) => (prev ? { ...prev, adminNote: e.target.value } : prev))} />
                </FormControl>
              </Stack>
            ) : null}
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="plain" onClick={() => { setEditTarget(null); setEditForm(null); }}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!editTarget || !editForm) return;
                  if (!editForm.fullName.trim()) {
                    setError('Full name is required.');
                    return;
                  }
                  if (toLocalPhone(editForm.phone).length !== 10) {
                    setError('Phone must be exactly 10 digits.');
                    return;
                  }
                  if (toLocalPhone(editForm.whatsAppNumber).length !== 10) {
                    setError('WhatsApp number must be exactly 10 digits.');
                    return;
                  }
                  if (editForm.email.trim() && !emailRegex.test(editForm.email.trim())) {
                    setError('Enter a valid email address.');
                    return;
                  }
                  if (!editForm.address.trim()) {
                    setError('Address is required.');
                    return;
                  }
                  if (Number(editForm.pledgeAmount || 0) <= 0) {
                    setError('Please enter a valid pledge amount.');
                    return;
                  }
                  setError('');

                  const updatedAt = new Date().toISOString();
                  const nextPledgeAmount = Number(editForm.pledgeAmount || 0);
                  const normalizedPhone = toLocalPhone(editForm.phone);
                  const normalizedWhatsApp = toLocalPhone(editForm.whatsAppNumber);
                  const nextSubmissionMode = editForm.submissionMode === 'auto_razorpay' ? 'online_transfer' : editForm.submissionMode;

                  await updateNityaSevaEnrollment(editTarget.id, {
                    fullName: editForm.fullName.trim(),
                    phone: normalizedPhone,
                    whatsAppNumber: normalizedWhatsApp,
                    email: editForm.email.trim(),
                    address: editForm.address.trim(),
                    dateOfBirth: editForm.dateOfBirth || '',
                    anniversaryDate: editForm.anniversaryDate || '',
                    startDate: editForm.startDate || undefined,
                    pledgeAmount: nextPledgeAmount,
                    paymentFrequency: editForm.paymentFrequency,
                    submissionMode: nextSubmissionMode,
                    panNumber: editForm.panNumber.trim().toUpperCase(),
                    status: editForm.status,
                    adminNote: editForm.adminNote,
                  });
                  applyEnrollmentPatch(editTarget.id, {
                    fullName: editForm.fullName.trim(),
                    phone: normalizedPhone,
                    whatsAppNumber: normalizedWhatsApp,
                    email: editForm.email.trim(),
                    address: editForm.address.trim(),
                    dateOfBirth: editForm.dateOfBirth || '',
                    anniversaryDate: editForm.anniversaryDate || '',
                    createdAt: editForm.startDate || editTarget.createdAt,
                    pledgeAmount: nextPledgeAmount,
                    pledgeTier: getPledgeTierFromAmount(nextPledgeAmount),
                    paymentFrequency: editForm.paymentFrequency,
                    submissionMode: nextSubmissionMode,
                    panNumber: editForm.panNumber.trim().toUpperCase(),
                    status: editForm.status,
                    adminNote: editForm.adminNote,
                    updatedAt,
                  });
                  setEditTarget(null);
                  setEditForm(null);
                  await refreshCurrentViewAfterMutation();
                }}
              >
                Save
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>

        <Modal open={offlineOpen} onClose={closeOfflineModal} sx={{ zIndex: 12001 }}>
          <ModalDialog sx={{ width: 'min(96vw, 680px)', maxHeight: '92vh', overflow: 'auto', zIndex: 12002 }}>
            <ModalClose />
            <Typography level="title-lg">Add Offline Nitya Seva Enrollment</Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              Enter phone and select a matched account if shown. If no account exists, a new offline user account will be created automatically.
            </Typography>

            {offlineError ? <Alert color="danger">{offlineError}</Alert> : null}

            <FormControl error={Boolean(offlineFieldErrors.phone)}>
              <FormLabel>Phone *</FormLabel>
              <Input
                value={offlineForm.phone}
                disabled={existingUserIdentityLocked}
                onChange={(e) => {
                  const local = toLocalPhone(e.target.value);
                  setOfflineForm((prev) => ({ ...prev, phone: local }));
                  setOfflineLookup(null);
                  setOfflineFieldErrors((prev) => ({ ...prev, phone: '' }));
                }}
                placeholder="10-digit phone"
              />
              {offlineFieldErrors.phone ? <FormHelperText>{offlineFieldErrors.phone}</FormHelperText> : null}
            </FormControl>

            {hasSelectedExistingUser ? (
              <Alert color="success" variant="soft">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Typography level="body-sm">Selected user #{offlineLookup?.user?.id}. Name and Phone/User ID are locked for data integrity.</Typography>
                  <Button
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => {
                      lastAutoSelectedRef.current = '';
                      setOfflineForm(defaultOfflineEnrollmentForm);
                      setOfflineLookup(null);
                      setPhoneSuggestions([]);
                      setPhoneSuggestionError('');
                      setOfflineFieldErrors({});
                      setOfflineError('');
                    }}
                  >
                    Change Phone/User
                  </Button>
                </Stack>
              </Alert>
            ) : null}

            {phoneSuggestionLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '18px' }} />
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Searching accounts...</Typography>
              </Stack>
            ) : null}

            {phoneSuggestions.length > 0 ? (
              <Box>
                <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                  Matching accounts — click to auto-fill:
                </Typography>
                <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 0.75, maxHeight: 200, overflow: 'auto' }}>
                  <Stack spacing={0.5}>
                    {phoneSuggestions.map((user) => (
                      <Button
                        key={user.id}
                        variant="soft"
                        color={user.hasEnrollment ? 'warning' : 'primary'}
                        endDecorator={
                          <Chip size="sm" variant="soft" color={user.hasEnrollment ? 'warning' : 'success'}>
                            {user.hasEnrollment ? 'Enrolled' : 'Select'}
                          </Chip>
                        }
                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                        onClick={() => applySuggestion(user)}
                      >
                        <Box>
                          <Typography level="body-sm" fontWeight="lg">
                            #{user.id} {user.legalName || 'Unnamed'}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {toLocalPhone(user.phone || user.whatsAppNumber || '')} · {user.email || 'No email'}
                          </Typography>
                        </Box>
                      </Button>
                    ))}
                  </Stack>
                </Sheet>
              </Box>
            ) : null}

            {phoneSuggestionError ? (
              <Alert color="warning" variant="soft">{phoneSuggestionError}</Alert>
            ) : null}

            {!phoneSuggestionLoading && toLocalPhone(offlineForm.phone).length === 10 && !phoneSuggestions.length ? (
              <Alert color="neutral" variant="soft">
                No user matched this 10-digit phone. You can continue and create a new offline user + enrollment.
              </Alert>
            ) : null}

            {offlineLookup?.found && offlineLookup.user ? (
              <Alert color={offlineLookup.existingEnrollment ? 'danger' : 'success'} variant="soft">
                {offlineLookup.existingEnrollment
                  ? `Existing user found (#${offlineLookup.user.id}) and Nitya Seva enrollment already exists.`
                  : `Existing user found (#${offlineLookup.user.id}). Enrollment will be linked to this account.`}
              </Alert>
            ) : null}

            <FormControl error={Boolean(offlineFieldErrors.fullName)}>
              <FormLabel>Full Name *</FormLabel>
              <Input
                value={offlineForm.fullName}
                disabled={existingUserIdentityLocked}
                onChange={(e) => {
                  setOfflineForm((prev) => ({ ...prev, fullName: e.target.value }));
                  setOfflineFieldErrors((prev) => ({ ...prev, fullName: '' }));
                }}
              />
              {offlineFieldErrors.fullName ? <FormHelperText>{offlineFieldErrors.fullName}</FormHelperText> : null}
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl sx={{ flex: 1 }} error={Boolean(offlineFieldErrors.whatsAppNumber)}>
                <FormLabel>WhatsApp Number *</FormLabel>
                <Input
                  value={offlineForm.whatsAppNumber}
                  onChange={(e) => {
                    const local = toLocalPhone(e.target.value);
                    setOfflineForm((prev) => ({ ...prev, whatsAppNumber: local }));
                    setOfflineFieldErrors((prev) => ({ ...prev, whatsAppNumber: '' }));
                  }}
                  placeholder="Defaults to phone"
                />
                {offlineFieldErrors.whatsAppNumber ? <FormHelperText>{offlineFieldErrors.whatsAppNumber}</FormHelperText> : null}
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={Boolean(offlineFieldErrors.email)}>
                <FormLabel>E-mail ID (optional)</FormLabel>
                <Input
                  type="email"
                  value={offlineForm.email}
                  disabled={existingEmailLocked}
                  onChange={(e) => {
                    setOfflineForm((prev) => ({ ...prev, email: e.target.value }));
                    setOfflineFieldErrors((prev) => ({ ...prev, email: '' }));
                  }}
                />
                {offlineFieldErrors.email ? <FormHelperText>{offlineFieldErrors.email}</FormHelperText> : null}
              </FormControl>
            </Stack>

            <FormControl error={Boolean(offlineFieldErrors.address)}>
              <FormLabel>Detailed Address *</FormLabel>
              <Textarea
                minRows={2}
                value={offlineForm.address}
                onChange={(e) => {
                  setOfflineForm((prev) => ({ ...prev, address: e.target.value }));
                  setOfflineFieldErrors((prev) => ({ ...prev, address: '' }));
                }}
              />
              {offlineFieldErrors.address ? <FormHelperText>{offlineFieldErrors.address}</FormHelperText> : null}
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl sx={{ flex: 1 }} error={Boolean(offlineFieldErrors.state)}>
                <FormLabel>State *</FormLabel>
                <Select
                  value={offlineForm.state}
                  placeholder="Select state"
                  sx={{ '--unstable_popup-zIndex': 12003 }}
                  slotProps={{ listbox: { sx: { zIndex: 12003 } } }}
                  onChange={(_, val) => {
                    setOfflineForm((prev) => ({ ...prev, state: (val as string) || '', city: '' }));
                    setOfflineFieldErrors((prev) => ({ ...prev, state: '', city: '' }));
                  }}
                >
                  {states.map((stateOption) => (
                    <Option key={stateOption} value={stateOption}>{stateOption}</Option>
                  ))}
                </Select>
                {offlineFieldErrors.state ? <FormHelperText>{offlineFieldErrors.state}</FormHelperText> : null}
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={Boolean(offlineFieldErrors.city)}>
                <FormLabel>City *</FormLabel>
                <Autocomplete
                  freeSolo
                  placeholder="City"
                  value={offlineForm.city}
                  options={cityOptions}
                  sx={{ '--unstable_popup-zIndex': 12003 }}
                  slotProps={{ listbox: { sx: { zIndex: 12003 } } }}
                  onInputChange={(_, newInputValue) => {
                    const upper = newInputValue.toUpperCase();
                    setOfflineForm((prev) => ({ ...prev, city: upper }));
                    setOfflineFieldErrors((prev) => ({ ...prev, city: '' }));
                  }}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : '')}
                />
                {offlineFieldErrors.city ? <FormHelperText>{offlineFieldErrors.city}</FormHelperText> : null}
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={Boolean(offlineFieldErrors.pincode)}>
                <FormLabel>Pincode *</FormLabel>
                <Input
                  placeholder="6-digit pincode"
                  value={offlineForm.pincode}
                  onChange={(e) => {
                    const next = digitsOnly(e.target.value).slice(0, 6);
                    setOfflineForm((prev) => ({ ...prev, pincode: next }));
                    setOfflineFieldErrors((prev) => ({ ...prev, pincode: '' }));
                  }}
                />
                {offlineFieldErrors.pincode ? <FormHelperText>{offlineFieldErrors.pincode}</FormHelperText> : null}
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel>Country</FormLabel>
              <Input value={offlineForm.country || 'India'} disabled />
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl sx={{ flex: 1 }} error={Boolean(offlineFieldErrors.dateOfBirth)}>
                <FormLabel>Date of Birth</FormLabel>
                <Input
                  sx={datePickerInputSx}
                  type="date"
                  slotProps={{ input: { max: '9999-12-31' } }}
                  value={offlineForm.dateOfBirth}
                  onChange={(e) => {
                    setOfflineForm((prev) => ({ ...prev, dateOfBirth: e.target.value }));
                    setOfflineFieldErrors((prev) => ({ ...prev, dateOfBirth: '' }));
                  }}
                />
                {offlineFieldErrors.dateOfBirth ? <FormHelperText>{offlineFieldErrors.dateOfBirth}</FormHelperText> : null}
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Date of Anniversary (if applicable)</FormLabel>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Input
                    sx={[datePickerInputSx, { flex: 1 }]}
                    type="date"
                    slotProps={{ input: { max: '9999-12-31' } }}
                    value={offlineForm.anniversaryDate}
                    onChange={(e) => setOfflineForm((prev) => ({ ...prev, anniversaryDate: e.target.value }))}
                  />
                  {offlineForm.anniversaryDate ? (
                    <Button
                      variant="soft"
                      color="danger"
                      onClick={() => setOfflineForm((prev) => ({ ...prev, anniversaryDate: '' }))}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Stack>
              </FormControl>
            </Stack>

            <FormControl error={Boolean(offlineFieldErrors.startDate)}>
              <FormLabel>Nitya Seva Start Date *</FormLabel>
              <Input
                sx={datePickerInputSx}
                type="date"
                slotProps={{ input: { max: '9999-12-31' } }}
                value={offlineForm.startDate}
                onChange={(e) => {
                  setOfflineForm((prev) => ({ ...prev, startDate: e.target.value }));
                  setOfflineFieldErrors((prev) => ({ ...prev, startDate: '' }));
                }}
              />
              {offlineFieldErrors.startDate ? <FormHelperText>{offlineFieldErrors.startDate}</FormHelperText> : null}
              <FormHelperText>Use the real join date when back-entering older offline enrollments.</FormHelperText>
            </FormControl>

            <FormControl error={Boolean(offlineFieldErrors.pledgeAmount)}>
              <FormLabel>Pledge Amount *</FormLabel>
              <Input
                type="number"
                value={offlineForm.pledgeAmount || ''}
                slotProps={{ input: { min: 1, step: 1 } }}
                onChange={(e) => {
                  const pledgeAmount = parsePledgeAmount(e.target.value);
                  setOfflineForm((prev) => ({
                    ...prev,
                    pledgeAmount,
                  }));
                  setOfflineFieldErrors((prev) => ({ ...prev, pledgeAmount: '' }));
                }}
              />
              {offlineFieldErrors.pledgeAmount ? <FormHelperText>{offlineFieldErrors.pledgeAmount}</FormHelperText> : null}
              <FormHelperText>Amount below Rs. 1100 will be enrolled under the Sevak tier.</FormHelperText>
            </FormControl>

            <Alert color={Number(offlineForm.pledgeAmount || 0) > 0 ? 'primary' : 'warning'} variant="soft">
              Pledge Tier: {getPledgeTierFromAmount(Number(offlineForm.pledgeAmount || 0))}
            </Alert>

            <FormControl>
              <FormLabel>Donation Frequency *</FormLabel>
              <RadioGroup
                value={offlineForm.paymentFrequency}
                onChange={(e) => setOfflineForm((prev) => ({ ...prev, paymentFrequency: e.target.value as NityaSevaPaymentFrequency }))}
              >
                {paymentFrequencyOptions.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    label={`${option.label} (${option.months === 1 ? 'pay every month' : `pay once for ${option.months} months`})`}
                  />
                ))}
              </RadioGroup>
              <FormHelperText>Payment becomes due from the first month of each selected cycle.</FormHelperText>
            </FormControl>

            <Alert color={Number(offlineForm.pledgeAmount || 0) > 0 ? 'primary' : 'neutral'} variant="soft">
              Cycle Amount: ₹{(Number(offlineForm.pledgeAmount || 0) * getPaymentFrequencyMonths(offlineForm.paymentFrequency)).toLocaleString('en-IN')} payable once every {getPaymentFrequencyLabel(offlineForm.paymentFrequency).toLowerCase()} cycle.
            </Alert>

            <FormControl>
                <FormLabel>Submission Mode *</FormLabel>
                <RadioGroup
                  value={offlineForm.submissionMode}
                  onChange={(e) => setOfflineForm((prev) => ({ ...prev, submissionMode: e.target.value as NityaSevaEnrollment['submissionMode'] }))}
                >
                  <Radio value="ecs_nach" label="ECS / NACH (manual setup by ISKCON team)" />
                  <Radio value="online_transfer" label="Online transfer every month" />
                  <Radio value="cash_first_sunday" label="In Cash on 1st Sunday of every month" />
                </RadioGroup>
              </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Status</FormLabel>
                <Select value={offlineForm.status} onChange={(_, val) => setOfflineForm((prev) => ({ ...prev, status: (val as string) || 'pending' }))}>
                  <Option value="pending">Pending</Option>
                  <Option value="active">Active</Option>
                  <Option value="paused">Paused</Option>
                  <Option value="needs_follow_up">Needs Follow Up</Option>
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={Boolean(offlineFieldErrors.panNumber)}>
                <FormLabel>PAN Number (optional)</FormLabel>
                <Input
                  value={offlineForm.panNumber}
                  onChange={(e) => {
                    setOfflineForm((prev) => ({ ...prev, panNumber: e.target.value.toUpperCase().slice(0, 10) }));
                    setOfflineFieldErrors((prev) => ({ ...prev, panNumber: '' }));
                  }}
                />
                {offlineFieldErrors.panNumber ? <FormHelperText>{offlineFieldErrors.panNumber}</FormHelperText> : null}
              </FormControl>
            </Stack>

            <FormControl>
              <FormLabel>Admin Note</FormLabel>
              <Textarea minRows={2} value={offlineForm.adminNote} onChange={(e) => setOfflineForm((prev) => ({ ...prev, adminNote: e.target.value }))} />
            </FormControl>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="plain" onClick={closeOfflineModal}>Cancel</Button>
              <Button loading={offlineSaving} onClick={saveOfflineEnrollment} disabled={Boolean(offlineLookup?.existingEnrollment)}>
                Save Enrollment
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>

        <Modal open={Boolean(manualTarget)} onClose={() => { if (!savingManualDonation) setManualTarget(null); }}>
          <ModalDialog sx={{ width: 'min(94vw, 540px)' }}>
            <ModalClose disabled={savingManualDonation} />
            <Typography level="title-lg">Record Manual Donation</Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {manualTarget ? `For ${manualTarget.fullName} • ${getPaymentFrequencyLabel(manualPaymentFrequency)} cycle` : 'Record donation'}
            </Typography>
            {savingManualDonation ? (
              <Alert color="primary" variant="soft">
                Recording donation and refreshing the list. Please wait...
              </Alert>
            ) : null}
            {manualTarget ? (
              <Alert color="primary" variant="soft">
                Monthly pledge: ₹{Number(manualTarget.pledgeAmount || 0).toLocaleString('en-IN')} • Suggested cycle amount: ₹{getCycleAmount(Number(manualTarget.pledgeAmount || 0), manualPaymentFrequency).toLocaleString('en-IN')}
              </Alert>
            ) : null}
            <FormControl>
              <FormLabel>Payment Frequency</FormLabel>
              <RadioGroup
                value={manualPaymentFrequency}
                onChange={(e) => {
                  const nextFrequency = e.target.value as NityaSevaPaymentFrequency;
                  setManualPaymentFrequency(nextFrequency);
                  if (manualTarget) {
                    setManualAmount(getCycleAmount(Number(manualTarget.pledgeAmount || 0), nextFrequency).toFixed(0));
                  }
                }}
              >
                {paymentFrequencyOptions.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    disabled={savingManualDonation}
                    label={`${option.label} (${option.months === 1 ? 'pay every month' : `pay once for ${option.months} months`})`}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <Input disabled={savingManualDonation} type="number" placeholder="Amount" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} />
            <Input disabled={savingManualDonation} sx={datePickerInputSx} type="month" value={manualForMonth} onChange={(e) => setManualForMonth(e.target.value)} />
            <Input disabled={savingManualDonation} sx={datePickerInputSx} type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
            <Input disabled={savingManualDonation} placeholder="Transaction ID (optional)" value={manualTransactionId} onChange={(e) => setManualTransactionId(e.target.value)} />
            <Select disabled={savingManualDonation} value={manualMethod} onChange={(_, val) => setManualMethod((val as string) || 'Cash')}>
              <Option value="Cash">Cash</Option>
              <Option value="Online">Online</Option>
              <Option value="Bank Transfer">Bank Transfer</Option>
            </Select>
            <Textarea disabled={savingManualDonation} minRows={3} placeholder="Note (optional)" value={manualNote} onChange={(e) => setManualNote(e.target.value)} />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="plain" disabled={savingManualDonation} onClick={() => setManualTarget(null)}>Cancel</Button>
              <Button
                loading={savingManualDonation}
                onClick={async () => {
                  if (!manualTarget) return;
                  setSavingManualDonation(true);
                  setError('');
                  try {
                    const targetId = manualTarget.id;
                    const manualDateIso = dayjs(manualDate).isValid() ? dayjs(manualDate).toISOString() : new Date().toISOString();
                    await recordNityaSevaManualDonation(manualTarget.id, {
                      amount: Number(manualAmount),
                      date: manualDate,
                      forMonth: manualForMonth,
                      paymentFrequency: manualPaymentFrequency,
                      paymentMethod: manualMethod,
                      transactionId: manualTransactionId,
                      note: manualNote,
                    });
                    applyEnrollmentPatch(targetId, {
                      lastManualDonationAt: manualDateIso,
                      updatedAt: new Date().toISOString(),
                    });
                    await maybeActivateEnrollmentIfNoDue(targetId);
                    setManualTarget(null);
                    await refreshCurrentViewAfterMutation();
                  } catch (_saveErr) {
                    setError('Failed to record manual donation. Please try again.');
                  } finally {
                    setSavingManualDonation(false);
                  }
                }}
              >
                Save Donation
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>

        <ExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          columns={viewMode === 'enrollments' ? enrollmentExportColumns : monthExportColumns}
          rows={viewMode === 'enrollments' ? enrollmentExportRows : monthExportRows}
          filename={viewMode === 'enrollments' ? 'nitya-seva-enrollments' : 'nitya-seva-months'}
          title={viewMode === 'enrollments' ? 'Nitya Seva Enrollments' : 'Nitya Seva Month Summary'}
        />
      </Box>
    </Box>
  );
}
